import axios from 'axios';
import apiClient from './apiClient';
import {
  isRecord,
  normalizeBoolean,
  normalizeIndex,
  normalizeNullableString,
  normalizeString,
} from './normalize';
import type {
  ContractSummary,
  SortDirection,
  UserDetail,
  UserPage,
  UserSortField,
  UserSummary,
} from './types';

/**
 * Service fuer die Benutzer-Endpunkte des MVGen-Management-Backends:
 * - GET /api/users            (Liste mit Suche, Paging, Sortierung)
 * - GET /api/users/{id}       (Stammdaten, inkl. officeExportEnabled)
 * - GET /api/users/{id}/contracts (Mietvertraege des Benutzers)
 * - PUT /api/users/{id}/office-export (Office-Export umschalten)
 *
 * Kein Caching (Projektstandard): Jeder Aufruf geht direkt ans Backend.
 *
 * Alle Antworten werden defensiv normalisiert (siehe ./normalize).
 * Listeneintraege ohne verwertbare ID werden verworfen, statt eine Zeile
 * ohne stabilen React-Key und ohne Detail-Verlinkung zu rendern.
 * Strukturell unbrauchbare Antworten (kein `content`-Array bzw. kein
 * Vertrags-Array) werfen dagegen, damit sie nicht als Leerzustand
 * erscheinen.
 *
 * Feldnamen: Die JSON-Namen entsprechen exakt den Backend-DTOs
 * (UserSummaryDTO, UserDetailDTO, ContractSummaryDTO, PageResponseDTO).
 * Bei den primitiven boolean-Feldern erzeugt Lombok `isXxx()`-Getter,
 * die Jackson auf die Property `xxx` abbildet — im JSON stehen also
 * `legalPerson`, `active`, `subscriptionActive`, `emailVerified` und
 * `pdfAvailable` (KEIN `is`-Praefix).
 */

/** Maximale Seitengroesse laut Backend-Vertrag (groesser => HTTP 400). */
export const MAX_PAGE_SIZE = 100;

/** Auswaehlbare Seitengroessen der Benutzerliste. */
export const PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100];

/** Vollstaendiger Abfragezustand der Benutzerliste. */
export interface UserListQuery {
  /** Suchbegriff; ein leerer Wert laesst den Parameter weg. */
  readonly search: string;
  /** Nullbasierte Seitennummer. */
  readonly page: number;
  /** Seitengroesse (1..MAX_PAGE_SIZE). */
  readonly size: number;
  /** Sortierfeld (Whitelist des Backends). */
  readonly sortField: UserSortField;
  /** Sortierrichtung. */
  readonly sortDirection: SortDirection;
}

/** Normalisiert einen Listeneintrag; null bei fehlender/kaputter ID. */
function normalizeUserSummary(payload: unknown): UserSummary | null {
  if (!isRecord(payload)) {
    return null;
  }
  const id: unknown = payload.id;
  if (typeof id !== 'string' || id === '') {
    return null;
  }
  return {
    id,
    username: normalizeString(payload.username, ''),
    email: normalizeString(payload.email, ''),
    firstName: normalizeNullableString(payload.firstName),
    lastName: normalizeNullableString(payload.lastName),
    companyName: normalizeNullableString(payload.companyName),
    // JSON-Feld heisst `legalPerson`: Das Backend-DTO haelt ein
    // `boolean legalPerson`, dessen Lombok-Getter `isLegalPerson()`
    // von Jackson auf die Property `legalPerson` abgebildet wird. Der
    // interne TS-Name bleibt `isLegalPerson` (liest sich als Flag).
    isLegalPerson: normalizeBoolean(payload.legalPerson),
    city: normalizeNullableString(payload.city),
    active: normalizeBoolean(payload.active),
    subscriptionActive: normalizeBoolean(payload.subscriptionActive),
    createdAt: normalizeString(payload.createdAt, ''),
  };
}

/**
 * Normalisiert eine Seite der Benutzerliste. page/size fallen auf die
 * angefragten Werte zurueck, totalPages wird notfalls aus
 * totalElements/size hergeleitet (sonst waere die Pagination nicht
 * bedienbar).
 *
 * Fehlt `content` als Array, ist die Antwort strukturell unbrauchbar und
 * wird wie ein Fehler behandelt (analog fetchUserDetail): Eine still zu
 * einer leeren Seite eingeebnete Antwort waere in der Anzeige nicht von
 * "keine Treffer" zu unterscheiden.
 */
function normalizeUserPage(payload: unknown, query: UserListQuery): UserPage {
  if (!isRecord(payload) || !Array.isArray(payload.content)) {
    throw new Error('Unbrauchbare Antwort des Benutzerlisten-Endpunkts');
  }
  const raw: Record<string, unknown> = payload;
  const content: readonly UserSummary[] = payload.content
    .map((entry: unknown) => normalizeUserSummary(entry))
    .filter((entry): entry is UserSummary => entry !== null);
  const size = Math.max(1, normalizeIndex(raw.size, query.size));
  const totalElements = normalizeIndex(raw.totalElements, content.length);
  return {
    content,
    page: normalizeIndex(raw.page, query.page),
    size,
    totalElements,
    totalPages: normalizeIndex(raw.totalPages, Math.ceil(totalElements / size)),
  };
}

/** Normalisiert die Stammdaten; null, wenn die Antwort unbrauchbar ist. */
function normalizeUserDetail(payload: unknown): UserDetail | null {
  const summary = normalizeUserSummary(payload);
  if (summary === null || !isRecord(payload)) {
    return null;
  }
  return {
    ...summary,
    street: normalizeNullableString(payload.street),
    houseNumber: normalizeNullableString(payload.houseNumber),
    postalCode: normalizeNullableString(payload.postalCode),
    phone: normalizeNullableString(payload.phone),
    authType: normalizeNullableString(payload.authType),
    emailVerified: normalizeBoolean(payload.emailVerified),
    updatedAt: normalizeString(payload.updatedAt, ''),
    officeExportEnabled: normalizeBoolean(payload.officeExportEnabled),
  };
}

/** Normalisiert einen Vertragseintrag; null bei fehlender/kaputter ID. */
function normalizeContractSummary(payload: unknown): ContractSummary | null {
  if (!isRecord(payload)) {
    return null;
  }
  const id: unknown = payload.id;
  if (typeof id !== 'string' || id === '') {
    return null;
  }
  return {
    id,
    contractNumber: normalizeNullableString(payload.contractNumber),
    status: normalizeString(payload.status, ''),
    createdAt: normalizeString(payload.createdAt, ''),
    pdfGeneratedAt: normalizeNullableString(payload.pdfGeneratedAt),
    pdfAvailable: normalizeBoolean(payload.pdfAvailable),
  };
}

/**
 * true, wenn der Fehler eine HTTP-404-Antwort ist (unbekannte ID).
 * Kapselt die axios-Abhaengigkeit, damit die Hooks sie nicht brauchen.
 */
export function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

/**
 * Laedt eine Seite der Benutzerliste.
 * Der Suchbegriff wird getrimmt und nur gesendet, wenn er nicht leer ist;
 * die Seitengroesse wird auf MAX_PAGE_SIZE begrenzt, damit ein
 * fehlerhafter Aufrufer keinen HTTP 400 provoziert.
 */
export async function fetchUsers(query: UserListQuery, signal?: AbortSignal): Promise<UserPage> {
  const params: Record<string, string> = {
    page: String(Math.max(0, query.page)),
    size: String(Math.min(MAX_PAGE_SIZE, Math.max(1, query.size))),
    sort: `${query.sortField},${query.sortDirection}`,
  };
  const search = query.search.trim();
  if (search !== '') {
    params.search = search;
  }
  const response = await apiClient.get<unknown>('/api/users', { params, signal });
  return normalizeUserPage(response.data, query);
}

/**
 * Laedt die Stammdaten eines Benutzers.
 * Eine strukturell unbrauchbare Antwort wird wie ein Fehler behandelt.
 */
export async function fetchUserDetail(userId: string, signal?: AbortSignal): Promise<UserDetail> {
  const response = await apiClient.get<unknown>(`/api/users/${encodeURIComponent(userId)}`, {
    signal,
  });
  const detail = normalizeUserDetail(response.data);
  if (detail === null) {
    throw new Error('Unbrauchbare Antwort des Benutzer-Endpunkts');
  }
  return detail;
}

/**
 * Laedt die Mietvertraege eines Benutzers (leeres Array, wenn der
 * Benutzer keine hat).
 *
 * Ist die Antwort kein Array, ist sie strukturell unbrauchbar und wird
 * wie ein Fehler behandelt (analog fetchUserDetail) — sonst waere eine
 * kaputte Antwort in der Anzeige nicht von "keine Vertraege vorhanden"
 * zu unterscheiden.
 */
export async function fetchUserContracts(
  userId: string,
  signal?: AbortSignal
): Promise<readonly ContractSummary[]> {
  const response = await apiClient.get<unknown>(
    `/api/users/${encodeURIComponent(userId)}/contracts`,
    { signal }
  );
  const payload: unknown = response.data;
  if (!Array.isArray(payload)) {
    throw new Error('Unbrauchbare Antwort des Vertrags-Endpunkts');
  }
  return payload
    .map((entry: unknown) => normalizeContractSummary(entry))
    .filter((entry): entry is ContractSummary => entry !== null);
}

/**
 * Schaltet den Office-Export eines Benutzers um und liefert den vom
 * Backend BESTAETIGTEN Wert zurueck — dieser ist immer zu uebernehmen,
 * nicht der angefragte `enabled`-Wert.
 *
 * Die Antwort wird defensiv geprueft: `officeExportEnabled` muss ein
 * boolean sein und `userId` muss der angefragten ID entsprechen, sonst
 * ist die Antwort strukturell unbrauchbar und wird wie ein Fehler
 * behandelt (analog fetchUserDetail/fetchUserContracts).
 */
export async function updateOfficeExport(
  userId: string,
  enabled: boolean,
  signal?: AbortSignal
): Promise<boolean> {
  const response = await apiClient.put<unknown>(
    `/api/users/${encodeURIComponent(userId)}/office-export`,
    { enabled },
    { signal }
  );
  const payload: unknown = response.data;
  if (
    !isRecord(payload) ||
    typeof payload.officeExportEnabled !== 'boolean' ||
    payload.userId !== userId
  ) {
    throw new Error('Unbrauchbare Antwort des Office-Export-Endpunkts');
  }
  return payload.officeExportEnabled;
}
